import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { DndContext } from '@dnd-kit/core';
import { selectChatContactId, selectIsChatAccepted, useChatStore } from 'stores/nic/stores/chatStore';
import { useCallStates, isCallInProgress } from '../hooks/useCallStates';
import { useCallTimer, useHoldTimer } from '../hooks/useCallTimer';
import { ActiveSession } from '../Views/ActiveSession';
import InactiveTabView from '../Views/InactiveTabView';
import ChatView from '../Views/Chat';
import DraggableWindow from './DraggableWindow';
import { useNicWindowSession } from './hooks/useNicWindowSession';
import { useInitializeWithScript } from './hooks/useInitializeWithScript';
import { useNicDirectoryData } from './hooks/useNicDirectoryData';
import { useIncomingCallRingtone } from './hooks/useIncomingCallRingtone';
import { useIncomingMessageSound } from './hooks/useIncomingMessageSound';
import { StyledHiddenAudio, StyledSessionMessage } from './styled';
/**
 * Owns everything that must survive route changes: the `#cxone-audio` element
 * handed to the voice SDK, the ringtone, the call timers and the draggable
 * window. Mounted once in the app shell (outside `<Routes>`) — if this
 * lived in the per-page `Header` instead, navigating would unmount the audio
 * element and the SDK would keep streaming into a detached node, killing sound
 * mid-call.
 */
var NICWindow = function () {
    var _a = useNicWindowSession(), isSessionActive = _a.isSessionActive, isWindowOpen = _a.isWindowOpen, isWebRtcOwner = _a.isWebRtcOwner, setIsWindowOpen = _a.setIsWindowOpen, hasActiveCall = _a.hasActiveCall, currentCall = _a.currentCall, isOnHold = _a.isOnHold;
    var isIncomingCall = useCallStates(currentCall).isIncomingCall;
    var isActiveCall = isCallInProgress(currentCall === null || currentCall === void 0 ? void 0 : currentCall.status);
    var callTimer = useCallTimer(isActiveCall);
    var holdTimer = useHoldTimer(isOnHold);
    var chatContactId = useChatStore(selectChatContactId);
    var isChatAccepted = useChatStore(selectIsChatAccepted);
    var hasChat = isSessionActive && !!chatContactId;
    useInitializeWithScript();
    useNicDirectoryData({ isWindowOpen: isWindowOpen, isSessionActive: isSessionActive, isWebRtcOwner: isWebRtcOwner });
    useIncomingCallRingtone({
        isIncomingCall: isIncomingCall,
        isWebRtcOwner: isWebRtcOwner,
        contactId: currentCall === null || currentCall === void 0 ? void 0 : currentCall.contactID,
        setIsWindowOpen: setIsWindowOpen
    });
    useIncomingMessageSound({
        isIncomingChat: hasChat && !isChatAccepted,
        isWebRtcOwner: isWebRtcOwner,
        contactId: chatContactId,
        setIsWindowOpen: setIsWindowOpen
    });
    var _b = useState({ x: 200, y: 200 }), position = _b[0], setPosition = _b[1];
    var handleDragEnd = function (event) {
        var delta = event.delta;
        var windowWidth = window.innerWidth;
        var windowHeight = window.innerHeight;
        var elementWidth = 355; // min-width from styled component
        setPosition(function (prev) {
            var newX = prev.x + delta.x;
            var newY = prev.y + delta.y;
            // Ensure window stays within viewport bounds
            var clampedX = Math.max(0, Math.min(newX, windowWidth - elementWidth));
            var clampedY = Math.max(0, Math.min(newY, windowHeight - 100)); // Keep at least 100px visible
            return {
                x: clampedX,
                y: clampedY
            };
        });
    };
    // Auto-open the window when a call starts (including one picked up in another
    // tab). Only the false -> true edge opens it: reacting to `isWindowOpen` here
    // instead would make closing the window re-trigger this effect and reopen it,
    // leaving no way to hide the window while a call is in progress.
    var hadActiveCall = useRef(false);
    useEffect(function () {
        if (hasActiveCall && !hadActiveCall.current) {
            setIsWindowOpen(true);
        }
        hadActiveCall.current = hasActiveCall;
    }, [hasActiveCall, setIsWindowOpen]);
    // Respond to ownership ping from other tabs
    useEffect(function () {
        if (!isWebRtcOwner)
            return;
        var pingChannel = new BroadcastChannel('nic-owner-ping');
        pingChannel.onmessage = function (event) {
            var _a;
            if (((_a = event.data) === null || _a === void 0 ? void 0 : _a.type) === 'ping') {
                // Respond that this tab is the active owner
                pingChannel.postMessage({ type: 'pong' });
            }
        };
        return function () {
            pingChannel.close();
        };
    }, [isWebRtcOwner]);
    var canShowSession = isWindowOpen && isSessionActive && !hasChat;
    var showInactiveTab = canShowSession && !isWebRtcOwner;
    var showActiveSession = canShowSession && isWebRtcOwner;
    return (_jsx(DndContext, { onDragEnd: handleDragEnd, children: _jsxs(DraggableWindow, { isWindowOpen: isWindowOpen, position: position, callTimer: isActiveCall ? callTimer : null, holdTimer: isOnHold ? holdTimer : null, children: [_jsx(StyledHiddenAudio, { id: "cxone-audio", controls: true, autoPlay: true }), hasChat && _jsx(ChatView, {}), showInactiveTab && _jsx(InactiveTabView, {}), showActiveSession && _jsx(ActiveSession, {}), !isSessionActive && (_jsx(StyledSessionMessage, { children: "Start session to get the access to the NIC." }))] }) }));
};
export default NICWindow;
